import { useMemo } from "react";
import { usePortal } from "../context/PortalContext";
import { toNumber } from "../helpers/AssetHelpers/AssetHelpers";
import type { IPrefill } from "../modules/common/other/CalendlyWidget/CalendlyWidget";

type BookingCalendarType = "adviser" | "retirement-specialist";

const getConfiguredUrl = (
  productionUrl: string | undefined,
  devUrl: string | undefined
) => {
  const isDev = process.env.REACT_APP_ENV === "dev";
  const preferredUrl = isDev ? devUrl : productionUrl;

  return preferredUrl || "";
};

const ADVISER_URL = getConfiguredUrl(
  process.env.REACT_APP_CALENDLY_PORTAL_CALENDARS_ADVISER_APPOINTMENT,
  process.env.REACT_APP_CALENDLY_PORTAL_CALENDARS_ADVISER_APPOINTMENT_DEV
);

const SPECIALIST_URL = getConfiguredUrl(
  process.env.REACT_APP_CALENDLY_PORTAL_CALENDARS_RETIREMENT_SPECIALIST,
  process.env.REACT_APP_CALENDLY_PORTAL_CALENDARS_RETIREMENT_SPECIALIST_DEV
);

interface IAppointmentBookingCalendar {
  isLoading: boolean;
  selectedCalendarType?: BookingCalendarType;
  selectedCalendarUrl?: string;
  adviserCalendarUrl?: string;
  retirementSpecialistCalendarUrl?: string;
  prefill: {
    empty: IPrefill;
    client: IPrefill;
  };
}

interface ISelectBookingCalendar {
  totalFundAmount: number;
  fullQuoteCompleted: boolean;
  factFindCompleted: boolean;
}

const getCalendarType = (
  props: ISelectBookingCalendar
): BookingCalendarType => {
  // // -db commented out adviser/retirement calendar switch - hardcode retirement calendar only
  // const { totalFundAmount, fullQuoteCompleted, factFindCompleted } = props;
  // const qualifiedFundAmount =
  //   totalFundAmount > 50000 && totalFundAmount < 500000;
  // const hasCompletedMilestone = factFindCompleted || fullQuoteCompleted;

  // return qualifiedFundAmount && hasCompletedMilestone
  //   ? "adviser"
  //   : "retirement-specialist";
  return "retirement-specialist";
};

function useAppointmentBookingCalendar(): IAppointmentBookingCalendar {
  const { clientData, anonymousClientData, isLoading } = usePortal();

  return useMemo(() => {
    const bookingClientData = clientData ?? anonymousClientData;
    const emptyPrefill: IPrefill = {};
    const clientPrefill: IPrefill = {
      name: bookingClientData
        ? [
            "title" in bookingClientData ? bookingClientData.title : undefined,
            bookingClientData.firstName,
            bookingClientData.lastName,
          ]
            .filter(Boolean)
            .join(" ")
        : undefined,
      email: bookingClientData?.email,
      phone: bookingClientData?.primaryPhone,
      customAnswers: bookingClientData?.primaryPhone
        ? { a1: bookingClientData.primaryPhone }
        : undefined,
    };

    const adviserCalendarUrl = ADVISER_URL || undefined;
    const retirementSpecialistCalendarUrl = SPECIALIST_URL || undefined;

    const baseCalendarData = {
      adviserCalendarUrl,
      retirementSpecialistCalendarUrl,
      prefill: {
        empty: emptyPrefill,
        client: clientPrefill,
      },
    };

    if (isLoading || !bookingClientData) {
      return {
        ...baseCalendarData,
        isLoading: true,
      };
    }

    if (!bookingClientData.annuity) {
      return {
        ...baseCalendarData,
        isLoading: false,
        selectedCalendarType: "retirement-specialist",
        selectedCalendarUrl: retirementSpecialistCalendarUrl,
      };
    }

    const totalFundAmount = toNumber(bookingClientData.annuity.pensionFund) ?? 0;
    const milestones =
      "milestones" in bookingClientData.annuity
        ? bookingClientData.annuity.milestones
        : undefined;
    const fullQuoteCompleted =
      milestones?.some((m) => m.milestoneId === "100225") ?? false;
    const factFindCompleted =
      milestones?.some((m) => m.milestoneId === "100226") ?? false;

    const calendarType = getCalendarType({
      totalFundAmount,
      fullQuoteCompleted,
      factFindCompleted,
    });

    const selectedUrl =
      calendarType === "adviser"
        ? adviserCalendarUrl
        : retirementSpecialistCalendarUrl;

    return {
      ...baseCalendarData,
      isLoading: false,
      selectedCalendarType: calendarType,
      selectedCalendarUrl: selectedUrl,
    };
  }, [isLoading, clientData, anonymousClientData]);
}

export default useAppointmentBookingCalendar;
export type { BookingCalendarType, IAppointmentBookingCalendar };
